import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUsers,
  FaUser,
  FaCrown,
  FaShieldAlt,
  FaSearch,
  FaTimes,
  FaGavel,
  FaSpinner,
  FaBan,
  FaMicrophoneSlash,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFlag,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import { useChat } from "../../context/ChatContext";
import axios from "../../utils/axiosConfig";
import toast from "react-hot-toast";

const ChatSidebar = ({ onClose, isMobile = false }) => {
  const { user } = useSelector((state) => state.auth);
  const { activeUsers, activeUsersCount } = useChat();
  const [searchTerm, setSearchTerm] = useState("");
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [muteDuration, setMuteDuration] = useState(7);
  const [banDuration, setBanDuration] = useState(30);
  const [actionReason, setActionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const isAdminOrMod = user?.role === "admin" || user?.role === "moderator";

  useEffect(() => {
    checkDarkMode();
  }, []);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const filteredUsers = activeUsers.filter((u) =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const fetchPendingReports = async () => {
    if (!isAdminOrMod) return;
    setLoadingReports(true);
    try {
      const response = await axios.get("/api/chat/reports/pending");
      setReports(response.data.reports);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
    } finally {
      setLoadingReports(false);
    }
  };

  const handleReviewReport = async (
    reportId,
    status,
    actionTaken,
    adminNote,
  ) => {
    setActionLoading(reportId);
    try {
      await axios.put(`/api/chat/report/${reportId}/review`, {
        status,
        actionTaken,
        adminNote,
      });
      toast.success(`Report ${status}`);
      fetchPendingReports();
    } catch (error) {
      toast.error("Failed to review report");
    } finally {
      setActionLoading(null);
    }
  };

  const handleMuteUser = async (userId) => {
    setActionLoading(`mute-${userId}`);
    try {
      await axios.post(`/api/chat/user/${userId}/mute`, {
        duration: muteDuration,
        reason: actionReason,
      });
      toast.success(`User muted for ${muteDuration} days`);
      setSelectedUser(null);
      setActionReason("");
    } catch (error) {
      toast.error("Failed to mute user");
    } finally {
      setActionLoading(null);
    }
  };

  const handleBanUser = async (userId) => {
    setActionLoading(`ban-${userId}`);
    try {
      await axios.post(`/api/chat/user/${userId}/ban`, {
        duration: banDuration,
        reason: actionReason,
      });
      toast.success(`User banned for ${banDuration} days`);
      setSelectedUser(null);
      setActionReason("");
    } catch (error) {
      toast.error("Failed to ban user");
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    if (showAdminPanel && isAdminOrMod) {
      fetchPendingReports();
    }
  }, [showAdminPanel, isAdminOrMod]);

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: getCardBackground(),
        backdropFilter: "blur(20px)",
        borderLeft: `1px solid ${getBorderColor()}`,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "16px",
          borderBottom: `1px solid ${getBorderColor()}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              background: "rgba(139,92,246,0.2)",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaUsers size={14} color="#8b5cf6" />
          </div>
          <h3
            style={{
              fontSize: "14px",
              margin: 0,
              fontWeight: "600",
              color: "white",
            }}
          >
            Active Users ({activeUsersCount})
          </h3>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            width: "28px",
            height: "28px",
            borderRadius: "8px",
            cursor: "pointer",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = "rgba(255,255,255,0.25)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "rgba(255,255,255,0.15)")
          }
        >
          <FaTimes size={12} />
        </button>
      </div>

      {/* Search */}
      <div style={{ padding: "16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            background: getInputBackground(),
            borderRadius: "12px",
            padding: "8px 12px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <FaSearch size={12} color={getTextSecondary()} />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              background: "none",
              border: "none",
              outline: "none",
              fontSize: "12px",
              color: getTextColor(),
            }}
          />
        </div>
      </div>

      {/* Admin Panel Toggle */}
      {isAdminOrMod && (
        <button
          onClick={() => setShowAdminPanel(!showAdminPanel)}
          style={{
            margin: "0 16px 12px",
            padding: "8px",
            background: showAdminPanel
              ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
              : getCardBackground(),
            color: showAdminPanel ? "white" : getTextColor(),
            border: `1px solid ${getBorderColor()}`,
            borderRadius: "10px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            fontSize: "12px",
            fontWeight: "500",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        >
          <FaGavel size={12} />
          {showAdminPanel ? "Hide Admin Panel" : "Show Admin Panel"}
        </button>
      )}

      {/* Admin Panel */}
      <AnimatePresence>
        {showAdminPanel && isAdminOrMod && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              margin: "0 16px 16px",
              padding: "12px",
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "12px",
              maxHeight: "350px",
              overflowY: "auto",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "12px",
              }}
            >
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  background: "rgba(239,68,68,0.1)",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFlag size={12} color="#ef4444" />
              </div>
              <h4
                style={{
                  fontSize: "12px",
                  margin: 0,
                  fontWeight: "600",
                  color: "#ef4444",
                }}
              >
                Pending Reports ({reports.length})
              </h4>
            </div>
            {loadingReports ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <FaSpinner size={16} color="#8b5cf6" />
                </motion.div>
              </div>
            ) : reports.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  background: isDarkMode
                    ? "rgba(139,92,246,0.05)"
                    : "rgba(139,92,246,0.03)",
                  borderRadius: "8px",
                }}
              >
                <FaCheckCircle size={20} color="#10b981" />
                <p
                  style={{
                    fontSize: "11px",
                    marginTop: "8px",
                    color: getTextSecondary(),
                  }}
                >
                  No pending reports
                </p>
              </div>
            ) : (
              reports.map((report) => (
                <motion.div
                  key={report._id}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  style={{
                    marginBottom: "12px",
                    padding: "10px",
                    background: isDarkMode
                      ? "rgba(139,92,246,0.05)"
                      : "rgba(139,92,246,0.03)",
                    borderRadius: "10px",
                    border: `1px solid ${getBorderColor()}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "6px",
                      flexWrap: "wrap",
                      gap: "4px",
                    }}
                  >
                    <span style={{ fontSize: "11px", color: getTextColor() }}>
                      <strong>{report.reportedUser?.name}</strong> reported by{" "}
                      <strong>{report.reporter?.name}</strong>
                    </span>
                    <span
                      style={{
                        background: "rgba(239,68,68,0.1)",
                        color: "#ef4444",
                        padding: "2px 6px",
                        borderRadius: "12px",
                        fontSize: "9px",
                      }}
                    >
                      {report.reason}
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: "10px",
                      marginBottom: "8px",
                      wordBreak: "break-word",
                      color: getTextSecondary(),
                      padding: "6px",
                      background: getInputBackground(),
                      borderRadius: "6px",
                    }}
                  >
                    {report.message}
                  </p>
                  <div
                    style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}
                  >
                    <button
                      onClick={() =>
                        handleReviewReport(report._id, "dismissed", "none", "")
                      }
                      disabled={actionLoading === report._id}
                      style={{
                        padding: "4px 10px",
                        background: "linear-gradient(135deg, #6c757d, #5a6268)",
                        border: "none",
                        borderRadius: "6px",
                        color: "white",
                        fontSize: "10px",
                        cursor: "pointer",
                        opacity: actionLoading === report._id ? 0.7 : 1,
                      }}
                    >
                      {actionLoading === report._id ? (
                        <FaSpinner
                          style={{ animation: "spin 1s linear infinite" }}
                          size={8}
                        />
                      ) : (
                        "Dismiss"
                      )}
                    </button>
                    <button
                      onClick={() =>
                        handleReviewReport(
                          report._id,
                          "action_taken",
                          "warning",
                          "Warning issued",
                        )
                      }
                      disabled={actionLoading === report._id}
                      style={{
                        padding: "4px 10px",
                        background: "linear-gradient(135deg, #f59e0b, #d97706)",
                        border: "none",
                        borderRadius: "6px",
                        color: "#333",
                        fontSize: "10px",
                        cursor: "pointer",
                        opacity: actionLoading === report._id ? 0.7 : 1,
                      }}
                    >
                      Warning
                    </button>
                    <button
                      onClick={() =>
                        handleReviewReport(
                          report._id,
                          "action_taken",
                          "mute",
                          "Muted for 7 days",
                        )
                      }
                      disabled={actionLoading === report._id}
                      style={{
                        padding: "4px 10px",
                        background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                        border: "none",
                        borderRadius: "6px",
                        color: "white",
                        fontSize: "10px",
                        cursor: "pointer",
                        opacity: actionLoading === report._id ? 0.7 : 1,
                      }}
                    >
                      Mute
                    </button>
                    <button
                      onClick={() =>
                        handleReviewReport(
                          report._id,
                          "action_taken",
                          "ban",
                          "Banned for 30 days",
                        )
                      }
                      disabled={actionLoading === report._id}
                      style={{
                        padding: "4px 10px",
                        background: "linear-gradient(135deg, #ef4444, #dc2626)",
                        border: "none",
                        borderRadius: "6px",
                        color: "white",
                        fontSize: "10px",
                        cursor: "pointer",
                        opacity: actionLoading === report._id ? 0.7 : 1,
                      }}
                    >
                      Ban
                    </button>
                  </div>
                </motion.div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* User List */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
        {filteredUsers.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "30px",
              color: getTextSecondary(),
              background: getCardBackground(),
              borderRadius: "12px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <FaUser size={24} color={getTextSecondary()} />
            <p style={{ marginTop: "8px", fontSize: "12px" }}>No users found</p>
          </div>
        ) : (
          filteredUsers.map((activeUser, idx) => (
            <motion.div
              key={activeUser.userId || activeUser._id}
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: idx * 0.03 }}
              whileHover={{ x: 3 }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px",
                marginBottom: "8px",
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "12px",
                border: `1px solid ${getBorderColor()}`,
                cursor:
                  isAdminOrMod && activeUser.userId !== user?.id
                    ? "pointer"
                    : "default",
                transition: "all 0.2s",
              }}
              onClick={() => {
                if (
                  isAdminOrMod &&
                  activeUser.userId !== user?.id &&
                  activeUser.userId !== user?._id
                ) {
                  setSelectedUser(activeUser);
                }
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div style={{ position: "relative" }}>
                  <img
                    src={
                      activeUser.profilePicture ||
                      `https://ui-avatars.com/api/?name=${activeUser.name}&background=8b5cf6&color=fff&size=32&rounded=true`
                    }
                    alt={activeUser.name}
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid #8b5cf6",
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      bottom: "0",
                      right: "0",
                      width: "10px",
                      height: "10px",
                      background: "#10b981",
                      borderRadius: "50%",
                      border: `2px solid ${getCardBackground()}`,
                    }}
                  />
                </div>
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: "500",
                        color: getTextColor(),
                      }}
                    >
                      {activeUser.name}
                    </span>
                    {activeUser.role === "admin" && (
                      <FaCrown size={10} color="#f59e0b" />
                    )}
                    {activeUser.role === "moderator" && (
                      <FaShieldAlt size={10} color="#06b6d4" />
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: "9px",
                      color: getTextSecondary(),
                      marginTop: "2px",
                    }}
                  >
                    {activeUser.branch || "Student"}
                  </div>
                </div>
              </div>
              {isAdminOrMod && activeUser.userId !== user?.id && (
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    background: "rgba(139,92,246,0.1)",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaGavel size={12} color="#8b5cf6" />
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>

      {/* User Action Modal */}
      <AnimatePresence>
        {selectedUser && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
              padding: "20px",
            }}
            onClick={() => setSelectedUser(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 25 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "20px",
                padding: "24px",
                maxWidth: "380px",
                width: "90%",
                maxHeight: "90vh",
                overflow: "auto",
                border: `1px solid ${getBorderColor()}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    background: "rgba(139,92,246,0.1)",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaGavel size={16} color="#8b5cf6" />
                </div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    color: getTextColor(),
                  }}
                >
                  Moderate User
                </h3>
              </div>
              <p
                style={{
                  marginBottom: "16px",
                  fontSize: "13px",
                  color: getTextSecondary(),
                }}
              >
                User:{" "}
                <strong style={{ color: getTextColor() }}>
                  {selectedUser.name}
                </strong>
              </p>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "6px",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Mute Duration (days)
                </label>
                <select
                  value={muteDuration}
                  onChange={(e) => setMuteDuration(parseInt(e.target.value))}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "10px",
                    border: `2px solid ${getBorderColor()}`,
                    background: getInputBackground(),
                    color: getTextColor(),
                    fontSize: "13px",
                  }}
                >
                  <option value={1}>1 day</option>
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "6px",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Ban Duration (days)
                </label>
                <select
                  value={banDuration}
                  onChange={(e) => setBanDuration(parseInt(e.target.value))}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "10px",
                    border: `2px solid ${getBorderColor()}`,
                    background: getInputBackground(),
                    color: getTextColor(),
                    fontSize: "13px",
                  }}
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                  <option value={90}>90 days</option>
                  <option value={365}>1 year</option>
                </select>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "6px",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Reason (optional)
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  rows="2"
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "10px",
                    border: `2px solid ${getBorderColor()}`,
                    background: getInputBackground(),
                    color: getTextColor(),
                    resize: "vertical",
                    fontSize: "12px",
                  }}
                  placeholder="Enter reason for action..."
                />
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  flexWrap: "wrap",
                  marginBottom: "12px",
                }}
              >
                <button
                  onClick={() =>
                    handleMuteUser(selectedUser.userId || selectedUser._id)
                  }
                  disabled={actionLoading === `mute-${selectedUser.userId}`}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    opacity:
                      actionLoading === `mute-${selectedUser.userId}` ? 0.7 : 1,
                  }}
                >
                  {actionLoading === `mute-${selectedUser.userId}` ? (
                    <FaSpinner
                      style={{ animation: "spin 1s linear infinite" }}
                      size={12}
                    />
                  ) : (
                    <FaMicrophoneSlash size={12} />
                  )}
                  Mute User
                </button>
                <button
                  onClick={() =>
                    handleBanUser(selectedUser.userId || selectedUser._id)
                  }
                  disabled={actionLoading === `ban-${selectedUser.userId}`}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "linear-gradient(135deg, #ef4444, #dc2626)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    opacity:
                      actionLoading === `ban-${selectedUser.userId}` ? 0.7 : 1,
                  }}
                >
                  {actionLoading === `ban-${selectedUser.userId}` ? (
                    <FaSpinner
                      style={{ animation: "spin 1s linear infinite" }}
                      size={12}
                    />
                  ) : (
                    <FaBan size={12} />
                  )}
                  Ban User
                </button>
              </div>

              <button
                onClick={() => setSelectedUser(null)}
                style={{
                  width: "100%",
                  padding: "10px",
                  background: getCardBackground(),
                  border: `1px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontSize: "12px",
                  color: getTextColor(),
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = isDarkMode
                    ? "rgba(139,92,246,0.1)"
                    : "rgba(139,92,246,0.05)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                Cancel
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default ChatSidebar;
